// RecordingScene.js

import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import { PerspectiveCamera } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import { SharedAnimationScene } from "./SharedAnimationScene";

const RECORDING_CAMERA = {
	position: [0, 21, 35.5],
	rotation: [-0.5, 0, 0],
	zoom: 1.9,
	fov: 40,
};

const loadFFmpeg = async (ffmpeg) => {
	if (ffmpeg.loaded) {
		return;
	}

	const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd";

	await ffmpeg.load({
		coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
		wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, "application/wasm"),
	});
};

export const RecordingScene = ({
	shots,
	onRecordingComplete,
	onVideoReady, // New: callback when preloaded video is ready
	triggerDownload = false, // New: when true, download the preloaded video
	zoomLevel = 1,
}) => {
	const canvasRef = useRef();
	const mediaRecorderRef = useRef(null);
	const recordedChunksRef = useRef([]);
	const ffmpegRef = useRef(new FFmpeg());
	const preloadedVideoRef = useRef(null); // Store preloaded MP4 blob

	const [animationState, setAnimationState] = useState("stopped");
	const [completedCount, setCompletedCount] = useState(0);
	const [_conversionStatus, setConversionStatus] = useState("");
	const [_isTabVisible, setIsTabVisible] = useState(true);

	useEffect(() => {
		const handleVisibilityChange = () => {
			setIsTabVisible(!document.hidden);
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, []);

	useEffect(() => {
		loadFFmpeg(ffmpegRef.current).catch((err) => {
			console.warn("FFmpeg preload failed, will retry during conversion:", err);
		});
	}, []);

	const convertWebMToMP4 = async (webmBlob) => {
		const ffmpeg = ffmpegRef.current;

		try {
			await loadFFmpeg(ffmpeg);
			ffmpeg.on("log", ({ message }) => {});

			ffmpeg.on("progress", ({ progress, time }) => {
				console.log(
					//`FFmpeg progress: ${Math.round(progress * 100)}% (${time}s)`,
				);
				setConversionStatus(`Converting: ${Math.round(progress * 100)}%`);
			});

			//console.log("Writing input file...");
			await ffmpeg.writeFile("input.webm", await fetchFile(webmBlob));
			console.log("Input file written");

			await ffmpeg.exec([
				"-i",
				"input.webm",
				"-c:v",
				"libx264", // Use H.264 video codec
				"-preset",
				"ultrafast", // Fastest encoding (WASM is already slow)
				"-tune",
				"animation", // Optimized for animated content with flat colors
				"-crf",
				"23", // Good quality, faster than 20 (visually acceptable)
				"-pix_fmt",
				"yuv420p", // Ensure compatibility
				"-an", // No audio
				"-movflags",
				"+faststart",
				"-r",
				"30", // 30fps is sufficient for smooth playback, halves encoding time
				"output.mp4",
			]);
			console.log("Conversion complete");

			// Read the output file
			console.log("Reading output file...");
			const data = await ffmpeg.readFile("output.mp4");
			console.log("Output file size:", data.length, "bytes");

			// Clean up
			await ffmpeg.deleteFile("input.webm");
			await ffmpeg.deleteFile("output.mp4");

			// Convert to Blob
			return new Blob([data.buffer], { type: "video/mp4" });
		} catch (error) {
			console.error("FFmpeg conversion error:", error);
			throw error;
		}
	};

	// This effect runs once when the component mounts to start the whole process
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		// INFO: use native h264 if possible, fallback to vp9/vp8
		let possibleTypes = [
			"video/mp4;codecs=avc1",
			"video/webm;codecs=vp9",
			"video/webm;codecs=vp8",
		];

		possibleTypes = possibleTypes.filter((e) => {
			return MediaRecorder.isTypeSupported(e);
		});

		if (possibleTypes.length === 0) {
			console.error("can't find supported codecs");
			return;
		}

		const preferredCodec = possibleTypes[0];

		// 1. Set up the MediaRecorder
		// 30 FPS is sufficient for smooth animation and halves encoding time
		const stream = canvas.captureStream(30);
		mediaRecorderRef.current = new MediaRecorder(stream, {
			mimeType: preferredCodec,
			videoBitsPerSecond: 8000000,
		});

		mediaRecorderRef.current.ondataavailable = (event) => {
			if (event.data.size > 0) {
				recordedChunksRef.current.push(event.data);
			}
		};

		const isMp4 = preferredCodec.startsWith("video/mp4");
		const blobType = isMp4 ? "video/mp4" : "video/webm";

		mediaRecorderRef.current.onstop = async () => {
			const vidBlob = new Blob(recordedChunksRef.current, {
				type: blobType,
			});

			try {
				let mp4Blob = null;

				if (!isMp4) {
					setConversionStatus("Converting to MP4...");
					console.log("[Preload] Converting to MP4...");

					mp4Blob = await convertWebMToMP4(vidBlob);
					setConversionStatus("Complete!");
				} else {
					console.log("already mp4, no need to transcode");
					mp4Blob = vidBlob;
				}

				// Store the preloaded video
				preloadedVideoRef.current = mp4Blob;

				// Notify parent that video is ready
				if (onVideoReady) {
					onVideoReady(mp4Blob);
				}

				// If onRecordingComplete exists (legacy mode), trigger download immediately
				if (onRecordingComplete && !onVideoReady) {
					const url = URL.createObjectURL(mp4Blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = `basketball-animation-${Date.now()}.mp4`;
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					URL.revokeObjectURL(url);
					onRecordingComplete();
				}
			} catch (error) {
				console.error("Error converting video:", error);
				setConversionStatus(`Error: ${error.message}`);

				// Store WebM as fallback
				preloadedVideoRef.current = vidBlob;

				if (onVideoReady) {
					onVideoReady(vidBlob);
				}

				if (onRecordingComplete && !onVideoReady) {
					const url = URL.createObjectURL(vidBlob);
					const a = document.createElement("a");
					a.href = url;
					a.download = `basketball-animation-${Date.now()}.webm`;
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					URL.revokeObjectURL(url);
					onRecordingComplete();
				}
			}
		};

		mediaRecorderRef.current.start();
		console.log("[Preload] Recording animation...");

		setAnimationState("playing");
	}, [convertWebMToMP4, onRecordingComplete, onVideoReady]); // Empty dependency array ensures this runs only once

	useEffect(() => {
		if (
			animationState === "playing" &&
			shots.length > 0 &&
			completedCount >= shots.length
		) {
			setTimeout(() => {
				if (
					mediaRecorderRef.current &&
					mediaRecorderRef.current.state === "recording"
				) {
					mediaRecorderRef.current.stop();
				}
			}, 4000);
		}
	}, [completedCount, shots.length, animationState]);

	// Handle download trigger from parent
	useEffect(() => {
		if (triggerDownload && preloadedVideoRef.current) {
			const blob = preloadedVideoRef.current;
			const isMP4 = blob.type === "video/mp4";
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `basketball-animation-${Date.now()}.${isMP4 ? "mp4" : "webm"}`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);

			if (onRecordingComplete) {
				onRecordingComplete();
			}
		}
	}, [triggerDownload, onRecordingComplete]);

	return (
		<>
			{/* Status display */}
			{/* {conversionStatus && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(0, 0, 0, 0.85)",
            color: "white",
            padding: "20px 40px",
            borderRadius: "8px",
            zIndex: 9999,
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {conversionStatus}
        </div>
      )} */}

			{/*
        Hidden recording canvas - uses opacity:0 instead of off-screen positioning.
        iOS Safari aggressively optimizes off-screen canvases (left: -9999px) to ~0.2 DPR,
        but renders opacity:0 elements at full quality since they're technically on-screen.
      */}
			<div
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					width: "1280px",
					height: "720px",
					opacity: 0,
					pointerEvents: "none",
					zIndex: -9999,
				}}
			>
				<Canvas
					ref={canvasRef}
					shadows
					gl={{
						toneMapping: 0,
						antialias: true,
						powerPreference: "high-performance",
						alpha: false,
						stencil: false,
						preserveDrawingBuffer: true,
					}}
					dpr={2} // Fixed DPR of 2
				>
					<color attach="background" args={["#0a1929"]} />
					{/* Match the exact lighting setup from the visible canvas */}
					<ambientLight intensity={0.4} />
					<directionalLight
						position={[10, 20, 10]}
						intensity={0.5}
						castShadow
						shadow-mapSize-width={2048}
						shadow-mapSize-height={2048}
						shadow-camera-far={50}
						shadow-camera-left={-20}
						shadow-camera-right={20}
						shadow-camera-top={20}
						shadow-camera-bottom={-20}
					/>
					<spotLight
						position={[0, 25, 0]}
						angle={0.3}
						penumbra={1}
						intensity={0.25}
						castShadow
						target-position={[0, 0, 0]}
					/>

					<mesh position={[0, -0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
						<planeGeometry args={[1000, 1000]} />
						<meshStandardMaterial color="#031422" metalness={0} roughness={0} />
					</mesh>

					<PerspectiveCamera
						makeDefault
						{...RECORDING_CAMERA}
						zoom={RECORDING_CAMERA.zoom * zoomLevel}
					/>

					{/* Shared animation scene - guaranteed to match display canvas */}
					<SharedAnimationScene
						shots={shots}
						animationState={animationState}
						restartKey={0}
						onAnimationComplete={() => setCompletedCount((c) => c + 1)}
						statsAlwaysVisible={false}
						completedCount={completedCount}
					/>
				</Canvas>
			</div>
		</>
	);
};
